import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import StartupCard from "../components/StartupCard";
import { Button } from "../components/ui/button";

export default function Following() {
    const { user, ready } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("notifications");
    const [brands, setBrands] = useState([]);
    const [notifications, setNotifications] = useState([]);

    useEffect(() => {
        if (!ready) return;
        if (!user) { navigate("/login"); return; }
        api.get("/me/follows").then((r) => setBrands(r.data || []));
        api.get("/me/notifications").then((r) => {
            const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
            setNotifications(items);
        });
        // Mark as read
        api.post("/me/notifications/mark-read").catch(() => {});
    }, [ready, user, navigate]);

    if (!ready) return <div className="min-h-screen flex items-center justify-center text-white/50">Loading...</div>;

    return (
        <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20 min-h-screen">
            <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ your feed</p>
            <h1 data-testid="following-title" className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter uppercase mb-8">
                Following.
            </h1>

            <div className="flex gap-2 mb-10 border-b border-white/10">
                <button data-testid="following-tab-notifications" onClick={() => setTab("notifications")}
                    className={`py-3 px-5 font-mono-tag text-xs uppercase transition-colors ${tab === "notifications" ? "text-brand-saffron border-b-2 border-brand-saffron" : "text-white/60 hover:text-white"}`}>
                    Notifications ({notifications.length})
                </button>
                <button data-testid="following-tab-brands" onClick={() => setTab("brands")}
                    className={`py-3 px-5 font-mono-tag text-xs uppercase transition-colors ${tab === "brands" ? "text-brand-saffron border-b-2 border-brand-saffron" : "text-white/60 hover:text-white"}`}>
                    Brands ({brands.length})
                </button>
            </div>

            {tab === "brands" && (
                brands.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-md py-20 text-center">
                        <p className="font-display text-2xl mb-2">You aren't following anyone yet.</p>
                        <p className="text-white/60 text-sm mb-6">Follow brands to get their next drop first.</p>
                        <Link to="/discover"><Button className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold rounded-md">Discover brands</Button></Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[260px]">
                        {brands.map((b, i) => (
                            <StartupCard key={b.id} startup={b} variant={i % 5 === 0 ? "tall" : "default"} />
                        ))}
                    </div>
                )
            )}

            {tab === "notifications" && (
                notifications.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-md py-20 text-center">
                        <p className="font-display text-2xl mb-2">No new drops (yet).</p>
                        <p className="text-white/60 text-sm">When brands you follow post a drop, it lands here.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {notifications.map((n) => (
                            <Link key={n.id} to={`/startup/${n.startup_id}`} data-testid={`notification-${n.id}`}
                                className="flex gap-4 grain-card p-4 rounded-md hover:border-brand-saffron transition-colors">
                                {n.image_url ? (
                                    <img src={n.image_url} alt={n.title} className="w-20 h-20 rounded object-cover" />
                                ) : (
                                    <div className="w-20 h-20 rounded bg-brand-ink border border-white/10 flex items-center justify-center shrink-0">
                                        <span className="font-serif-editorial italic text-3xl text-white/20">{n.startup_name?.[0] || "P"}</span>
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-mono-tag text-[10px] uppercase text-brand-saffron">/ {n.startup_name}</p>
                                        <span className={`font-mono-tag text-[9px] uppercase px-1.5 py-0.5 rounded-sm ${n.type === "page_update" ? "bg-white/10 text-white/60" : "bg-brand-magenta/20 text-brand-magenta"}`}>
                                            {n.type === "page_update" ? "Brand update" : "New drop"}
                                        </span>
                                    </div>
                                    <p className="font-display font-bold text-lg tracking-tight leading-tight mt-1 truncate">{n.title}</p>
                                    <p className="text-white/60 text-xs mt-1 line-clamp-2">{n.description}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )
            )}
        </main>
    );
}
